import {
  createSlice,
  PayloadAction
} from '@reduxjs/toolkit';
// types
import { ALIGN_OPTION_VALUES, NullableGroupedAssignments } from '../components/Credential/Presentation/types/common';
import {
  DEFAULT_ASSIGNMENTS_COL_TEMPLATE_STANDARD, DEFAULT_ASSIGNMENTS_ROW_TEMPLATE_STANDARD, GRID_AREA_PREFIX, MAX_APPLIED_FIELDS
} from '../components/Credential/Presentation/util/standardTemplate';
import { getClosestValidColumn } from '../components/Credential/Presentation/util/gridFormats';
import { AssignmentTableColumnsKeysType, DEFAULT_APPLIED_FIELDS_STANDARD_TEMPLATES, DEFAULT_SORTING_STATE, groupAssignmentsByClientId, normalizePresentationSortingState } from '../util/common';
import { DEFAULT_TEMAPLTE } from '../components/Credential/Presentation/util/util';
import { CompanyOfficialLogo, CredentialTileSlotLayoutV1, CredRef, IBaseCredentialMetaData, ICredential, ITemplateSortingState, PresentationSlideNumber, PresentationSlideTitle, PrivateAndConfidential, TEMPLATE_KEYS_TYPE, TEMPLATE_META_DATA, TileHeaderData } from '../types/credential';
import { RootState } from '../../../store';
import { IAssigmentsTableData } from '../types/common';
import { createTileSlotLayoutFromLegacyGrid, moveTileInSlotLayout, sanitizeTileSlotLayout, syncTileSlotLayoutWithTileIds } from '../components/Credential/Presentation/util/tileSlotLayout';
import {
  buildTileSlotLayoutFromAssignments,
  buildTileSlotLayoutPreservingOrder,
  getSortedGroupedAssignmentsForLayout,
  getTileIdsFromGroupedAssignmentsForLayout,
  orderGroupedAssignmentsByTileSlotLayout,
} from '../components/Credential/Presentation/util/assignmentTileLayout';

export type SortableAsisgnmentTableColumnsKeysType = {
  id: string;
  columnKey: AssignmentTableColumnsKeysType;
}

type LayoutOptions = {
  colsPerRow: number;
  rowsPerSlide: number;
  isCompanyLogoVisible: boolean;
  assignmentFields: SortableAsisgnmentTableColumnsKeysType[];
}

export type PresentationState = {
  credRef: CredRef;
  // form state
  presentationSlideTitle: PresentationSlideTitle;
  privateAndConfidential: PrivateAndConfidential;
  companyOfficialLogo: CompanyOfficialLogo;
  presentationSlideNumber: PresentationSlideNumber;
  description: string;
  // sort state
  sortingState: ITemplateSortingState;
  // template
  templateKey: TEMPLATE_KEYS_TYPE;
  // layout state
  colsPerRow: number;
  rowsPerSlide: number;
  tileSlotLayout: CredentialTileSlotLayoutV1 | null;
  // use this only for presentation views, this doesnt store the exact assignments data from the Cred Object
  assignments: NullableGroupedAssignments;
  isCompanyLogoVisible: boolean;
  tileHeaderData: TileHeaderData;
  tilesAlignment: ALIGN_OPTION_VALUES;
  // css info -
  // ! THIS IS CRITICAL - FIX TYPES HERE - EMPTY ARR ELEMENT WONT BE KNOWN - TUPLE CAN HAVE UNDEFINED OR NOT PRESENT VALUE
  // @TODO: balraj better types here maybe - this is a tuple whose length is determined by MAX Allowed Applied Fields and each of the element in the type can be empty/optional
  // Also due to DND toolkit we are complicating type by adding 'id' - see if can remove
  assignmentFields: SortableAsisgnmentTableColumnsKeysType[];
}

export const initialState = {
  credRef: '',
  assignments: undefined,
  // form
  presentationSlideTitle: {
    title_text: '',
    repeat_on_all_slides: true,
  },
  privateAndConfidential: {
    placement: 'bottom-left',
  },
  companyOfficialLogo: {
    placement: 'bottom-right',
  },
  presentationSlideNumber: {
    placement: 'bottom-center',
    repeat_on_all_slides: true,
  },
  description: '',
  // sort state
  sortingState: DEFAULT_SORTING_STATE,
  // layout
  colsPerRow: DEFAULT_ASSIGNMENTS_COL_TEMPLATE_STANDARD,
  rowsPerSlide: DEFAULT_ASSIGNMENTS_ROW_TEMPLATE_STANDARD,
  tileSlotLayout: null,
  isCompanyLogoVisible: true,
  tileHeaderData: { colKey: null },
  tilesAlignment: ALIGN_OPTION_VALUES.LEFT,
  // eg - position title, start date
  assignmentFields: DEFAULT_APPLIED_FIELDS_STANDARD_TEMPLATES,
  templateKey: DEFAULT_TEMAPLTE,
} satisfies PresentationState as PresentationState;

const getDefaultTilesAlignment = (): ALIGN_OPTION_VALUES => ALIGN_OPTION_VALUES.LEFT;

export const presentationSlice = createSlice({
  name: "presentation",
  initialState,
  reducers: {
    updatePresentationSlideTitle: (state, action: PayloadAction<{
      titleText: PresentationSlideTitle['title_text'],
      repeatOnAllSlides: PresentationSlideTitle['repeat_on_all_slides'],
    }>) => {
      state.presentationSlideTitle = {
        repeat_on_all_slides: action.payload.repeatOnAllSlides,
        title_text: action.payload.titleText,
      }
    },
    updatePrivateAndConfidential: (state, action: PayloadAction<{
      placement: PrivateAndConfidential['placement'];
    }>) => {
      state.privateAndConfidential = {
        placement: action.payload.placement,
      }
    },
    updateCompanyOfficialLogo: (state, action: PayloadAction<{
      placement: CompanyOfficialLogo['placement'];
    }>) => {
      state.companyOfficialLogo = {
        placement: action.payload.placement,
      }
    },
    updatePresentationSlideNumber: (state, action: PayloadAction<{
      repeatOnAllSlides: boolean;
    }>) => {
      state.presentationSlideNumber.repeat_on_all_slides = action.payload.repeatOnAllSlides;
    },
    updateDescription: (state, action: PayloadAction<{ description: string }>) => {
      state.description = action.payload.description;
    },
    updateAssignments: (state, action: PayloadAction<{
      assignments: NullableGroupedAssignments;
      assignmentFieldsOverride: IBaseCredentialMetaData['assignment_fields_override'];
      sortingState: TEMPLATE_META_DATA['sorting_state'];
    }>) => {
      const assignmentFieldsOverride = action.payload.assignmentFieldsOverride;
      const sortingState = normalizePresentationSortingState(action.payload.sortingState);

      if (sortingState) {
        state.sortingState = sortingState;
      }

      state.assignments = getSortedGroupedAssignmentsForLayout({
        assignmentFieldsOverride,
        groupedAssignments: action.payload.assignments,
        sortingState,
      });
      state.tileSlotLayout = buildTileSlotLayoutPreservingOrder({
        colsPerRow: state.colsPerRow,
        groupedAssignments: state.assignments,
        rowsPerSlide: state.rowsPerSlide,
        tileSlotLayout: state.tileSlotLayout,
      });
    },
    updateLayout: (state, action: PayloadAction<{
      assignments: NullableGroupedAssignments;
      assignmentFieldsOverride: IBaseCredentialMetaData['assignment_fields_override'];
      sortingState: TEMPLATE_META_DATA['sorting_state'];
    }>) => {
      const assignmentFieldsOverride = action.payload.assignmentFieldsOverride;
      const sortingState = normalizePresentationSortingState(action.payload.sortingState);
      const assignments = action.payload.assignments;

      if (sortingState) {
        state.sortingState = sortingState;
      }

      state.assignments = getSortedGroupedAssignmentsForLayout({
        assignmentFieldsOverride,
        groupedAssignments: assignments,
        sortingState,
      });
      state.tileSlotLayout = buildTileSlotLayoutPreservingOrder({
        colsPerRow: state.colsPerRow,
        groupedAssignments: state.assignments,
        rowsPerSlide: state.rowsPerSlide,
        tileSlotLayout: state.tileSlotLayout,
      });
    },
    updateTileSlotLayoutOrder: (state, action: PayloadAction<{
      activeId: string;
      overId: string;
    }>) => {
      if (!state.tileSlotLayout) {
        return;
      }

      state.tileSlotLayout = moveTileInSlotLayout({
        activeId: action.payload.activeId,
        overId: action.payload.overId,
        tileSlotLayout: state.tileSlotLayout,
      });
      state.sortingState = {
        sorted_by_field: 'manual',
        value: 'manual',
      };
    },
    updateSortingState: (state, action: PayloadAction<{
      sortingState: ITemplateSortingState;
      assignmentFieldsOverride: IBaseCredentialMetaData['assignment_fields_override'];
    }>) => {
      const assignmentFieldsOverride = action.payload.assignmentFieldsOverride;
      const sortingState = normalizePresentationSortingState(
        action.payload.sortingState,
      ) ?? action.payload.sortingState;
      state.sortingState = sortingState;

      state.assignments = getSortedGroupedAssignmentsForLayout({
        assignmentFieldsOverride,
        groupedAssignments: state.assignments,
        sortingState,
      });
      state.tileSlotLayout = buildTileSlotLayoutFromAssignments({
        colsPerRow: state.colsPerRow,
        groupedAssignments: state.assignments,
        rowsPerSlide: state.rowsPerSlide,
      });
    },
    updateLayoutColsPerRow: (state, action: PayloadAction<{
      colsPerRow: LayoutOptions['colsPerRow'],
    }>) => {
      state.colsPerRow = action.payload.colsPerRow;
      state.tileSlotLayout = buildTileSlotLayoutPreservingOrder({
        colsPerRow: state.colsPerRow,
        groupedAssignments: state.assignments,
        rowsPerSlide: state.rowsPerSlide,
        tileSlotLayout: state.tileSlotLayout,
      });
    },
    updateLayoutRowsPerSlide: (state, action: PayloadAction<{
      rowsPerSlide: LayoutOptions['rowsPerSlide'],
    }>) => {
      state.rowsPerSlide = action.payload.rowsPerSlide;
      state.colsPerRow = getClosestValidColumn({
        rows: state.rowsPerSlide,
        preferredColumns: state.colsPerRow,
      });
      state.tileSlotLayout = buildTileSlotLayoutPreservingOrder({
        colsPerRow: state.colsPerRow,
        groupedAssignments: state.assignments,
        rowsPerSlide: state.rowsPerSlide,
        tileSlotLayout: state.tileSlotLayout,
      });
    },
    resetLayout: (state, action: PayloadAction<{
      assignmentFieldsOverride: IBaseCredentialMetaData['assignment_fields_override'];
    }>) => {
      state.colsPerRow = initialState.colsPerRow;
      state.rowsPerSlide = initialState.rowsPerSlide;
      state.assignmentFields = initialState.assignmentFields;
      state.isCompanyLogoVisible = initialState.isCompanyLogoVisible;
      state.tileHeaderData = initialState.tileHeaderData;
      state.tilesAlignment = getDefaultTilesAlignment();
      const assignmentFieldsOverride = action.payload.assignmentFieldsOverride;
      const sortingState = DEFAULT_SORTING_STATE;

      if (sortingState) {
        state.sortingState = sortingState;
      }

      state.assignments = getSortedGroupedAssignmentsForLayout({
        assignmentFieldsOverride,
        groupedAssignments: state.assignments,
        sortingState,
      });
      state.tileSlotLayout = buildTileSlotLayoutFromAssignments({
        colsPerRow: state.colsPerRow,
        groupedAssignments: state.assignments,
        rowsPerSlide: state.rowsPerSlide,
      });
    },
    toggleCompanyLogoVisibility: (state) => {
      state.isCompanyLogoVisible = !state.isCompanyLogoVisible;
    },
    toggleTilesAlignment: (state, action: PayloadAction<{
      assignmentFieldsOverride: IBaseCredentialMetaData['assignment_fields_override'];
    }>) => {
      state.tilesAlignment = state.tilesAlignment === ALIGN_OPTION_VALUES.LEFT ? ALIGN_OPTION_VALUES.CENTER : ALIGN_OPTION_VALUES.LEFT;
      const assignmentFieldsOverride = action.payload.assignmentFieldsOverride;

      state.assignments = getSortedGroupedAssignmentsForLayout({
        assignmentFieldsOverride,
        groupedAssignments: state.assignments,
        sortingState: state.sortingState,
      });
      state.tileSlotLayout = buildTileSlotLayoutFromAssignments({
        colsPerRow: state.colsPerRow,
        groupedAssignments: state.assignments,
        rowsPerSlide: state.rowsPerSlide,
      });
    },
    addAssignmentField: (state, action: PayloadAction<AssignmentTableColumnsKeysType>) => {
      if (state.assignmentFields.length === MAX_APPLIED_FIELDS) {
        state.assignmentFields[MAX_APPLIED_FIELDS - 1] = { id: action.payload, columnKey: action.payload };
      } else {
        state.assignmentFields.push({ id: action.payload, columnKey: action.payload });
      }
    },
    toggleTileHeaderData: (state, action: PayloadAction<{
      colKey: TileHeaderData['colKey'];
    }>) => {
      const colKey = action.payload.colKey;
      if (state.tileHeaderData.colKey === colKey) {
        state.tileHeaderData.colKey = null;
      } else {
        state.tileHeaderData.colKey = colKey;
      }
    },
    replaceAssignmentField: (state, action: PayloadAction<{
      oldColumnKey: AssignmentTableColumnsKeysType;
      newColumnKey: AssignmentTableColumnsKeysType;
    }>) => {
      const { oldColumnKey, newColumnKey } = action.payload;
      const index = state.assignmentFields.findIndex((field) => field.columnKey === oldColumnKey);
      if (index !== -1) {
        state.assignmentFields[index] = { id: newColumnKey, columnKey: newColumnKey };
      }
    },
    removeAssignmentField: (state, action: PayloadAction<AssignmentTableColumnsKeysType>) => {
      state.assignmentFields = state.assignmentFields.filter((appliedField) => appliedField.columnKey !== action.payload);
    },
    updateAssignmentField: (state, action: PayloadAction<SortableAsisgnmentTableColumnsKeysType[]>) => {
      state.assignmentFields = action.payload;
    },
    reset: (_state) => {
      return initialState;
    },
    buildStateFromCredByRef: (state, action: PayloadAction<{
      credential: ICredential;
    }>) => {
      const credential = action.payload.credential;
      const metadata = credential.metadata;
      // initialise client-side state from server-side state
      state.credRef = credential.ref;

      // assignemnts search api call gets skipped if nothing is shortlisted so the extra reducer in this reducer for that matchFulfilled doesnt get run
      if ((credential.shortlist?.length ?? 0) === 0) {
        Object.assign(state, {
          ...initialState,
          credRef: state.credRef,
          tilesAlignment: getDefaultTilesAlignment(),
        });

        return;
      }
      if (metadata) {
        const activeTemplate = metadata.active_template;
        const templateMetadata = metadata[activeTemplate];
        const css = templateMetadata?.css;

        // initialise client-side state from server-side state
        state.description = metadata[activeTemplate]?.slide_description ?? initialState.description;
        state.colsPerRow = metadata[activeTemplate]?.cols_per_row ?? initialState.colsPerRow;
        state.rowsPerSlide = metadata[activeTemplate]?.rows_per_slide ?? initialState.rowsPerSlide;
        state.assignmentFields = metadata[activeTemplate]?.assignment_fields?.map((field) => ({ id: field, columnKey: field })) ?? initialState.assignmentFields;
        state.isCompanyLogoVisible = metadata[activeTemplate]?.is_company_logo_visible ?? initialState.isCompanyLogoVisible;
        state.tilesAlignment = metadata[activeTemplate]?.tiles_alignment ?? getDefaultTilesAlignment();
        state.tileHeaderData = metadata[activeTemplate]?.tile_header_data ?? initialState.tileHeaderData;
        state.templateKey = activeTemplate;
        state.sortingState = normalizePresentationSortingState(
          metadata[activeTemplate]?.sorting_state,
        ) ?? initialState.sortingState;
        state.presentationSlideTitle = metadata[activeTemplate]?.presentation_slide_title ?? initialState.presentationSlideTitle;
        state.privateAndConfidential = metadata[activeTemplate]?.private_and_confidential ?? initialState.privateAndConfidential;
        state.companyOfficialLogo = metadata[activeTemplate]?.company_official_logo ?? initialState.companyOfficialLogo;
        state.presentationSlideNumber = metadata[activeTemplate]?.presentation_slide_number ?? initialState.presentationSlideNumber;
        state.tileSlotLayout = sanitizeTileSlotLayout({
          tileSlotLayout: templateMetadata?.tile_slot_layout,
        });

        if (!state.tileSlotLayout) {
          state.tileSlotLayout = createTileSlotLayoutFromLegacyGrid({
            colsPerRow: state.colsPerRow,
            gridTemplateAreas: css?.['grid-template-areas'],
            prefix: GRID_AREA_PREFIX,
            rowsPerSlide: state.rowsPerSlide,
          });
        }
        if (state.tileSlotLayout) {
          state.colsPerRow = state.tileSlotLayout.cols_per_row;
          state.rowsPerSlide = state.tileSlotLayout.rows_per_slide;
        }
      } else {
        state.description = initialState.description;
        state.colsPerRow = initialState.colsPerRow;
        state.rowsPerSlide = initialState.rowsPerSlide;
        state.tileSlotLayout = initialState.tileSlotLayout;
        state.assignmentFields = initialState.assignmentFields;
        state.isCompanyLogoVisible = initialState.isCompanyLogoVisible;
        state.tileHeaderData = initialState.tileHeaderData;
        state.tilesAlignment = getDefaultTilesAlignment();
        state.templateKey = initialState.templateKey;
        state.sortingState = initialState.sortingState;
        state.presentationSlideTitle = initialState.presentationSlideTitle;
        state.privateAndConfidential = initialState.privateAndConfidential;
        state.companyOfficialLogo = initialState.companyOfficialLogo;
        state.presentationSlideNumber = initialState.presentationSlideNumber;
      }
    },
    buildAssignmentsAndGridTemplate: (state, action: PayloadAction<{
      assignments: IAssigmentsTableData[];
      assignmentFieldsOverride: IBaseCredentialMetaData['assignment_fields_override'];
      sortingState: TEMPLATE_META_DATA['sorting_state'];
      assignmentsManual: IBaseCredentialMetaData['assignments_manual'];
    }>) => {
      const assignments = action.payload?.assignments ?? [];
      const sortingState = normalizePresentationSortingState(action.payload.sortingState);
      const assignmentFieldsOverride = action.payload.assignmentFieldsOverride;
      const assignmentsManual = action.payload.assignmentsManual;

      const { groupedByClientCompanyIdAssignments } = groupAssignmentsByClientId({
        assignments,
        assignmentFieldsOverride,
        sortingState,
        gridTemplateAreas: undefined,
        assignmentsManual,
      });

      const hasActiveFieldSort = sortingState?.sorted_by_field
        && sortingState.sorted_by_field !== 'manual'
        && (sortingState.value === 'asc' || sortingState.value === 'desc');

      // A field sort is stable for equal values. Seed that stable sort from the
      // canonical tile layout so a credential metadata update cannot replace
      // the visible tie order with the raw assignment-ref order after autosave.
      state.assignments = hasActiveFieldSort
        ? getSortedGroupedAssignmentsForLayout({
          assignmentFieldsOverride,
          groupedAssignments: orderGroupedAssignmentsByTileSlotLayout({
            groupedAssignments: groupedByClientCompanyIdAssignments,
            previousGroupedAssignments: state.assignments,
            tileSlotLayout: state.tileSlotLayout,
          }),
          sortingState,
        })
        : groupedByClientCompanyIdAssignments;

      state.tileSlotLayout = hasActiveFieldSort
        ? buildTileSlotLayoutFromAssignments({
          colsPerRow: state.colsPerRow,
          groupedAssignments: state.assignments,
          rowsPerSlide: state.rowsPerSlide,
        })
        : syncTileSlotLayoutWithTileIds({
          colsPerRow: state.colsPerRow,
          rowsPerSlide: state.rowsPerSlide,
          tileIds: getTileIdsFromGroupedAssignmentsForLayout(state.assignments),
          tileSlotLayout: state.tileSlotLayout,
        });
    }
  },
});

export const selectAssignments = (state: RootState) => state.presentation.assignments;
export const selectPresentationSlideTitle = (state: RootState) => state.presentation.presentationSlideTitle;
export const selectPrivateAndConfidential = (state: RootState) => state.presentation.privateAndConfidential;
export const selectCompanyLogoOfficial = (state: RootState) => state.presentation.companyOfficialLogo;
export const selectPresentationSlideNumber = (state: RootState) => state.presentation.presentationSlideNumber;
export const selectDescription = (state: RootState) => state.presentation.description;
export const selectColsPerRow = (state: RootState) => state.presentation.colsPerRow;
export const selectRowsPerSlide = (state: RootState) => state.presentation.rowsPerSlide;
export const selectTileSlotLayout = (state: RootState) => state.presentation.tileSlotLayout;
export const selectCompanyLogoVisibility = (state: RootState) => state.presentation.isCompanyLogoVisible;
export const selectTilesAlignment = (state: RootState) => state.presentation.tilesAlignment;
export const selectTileHeaderData = (state: RootState) => state.presentation.tileHeaderData?.colKey;
export const selectAppliedFields = (state: RootState) => state.presentation.assignmentFields;
export const selectTemplateKey = (state: RootState) => state.presentation.templateKey;
export const selectSortingState = (state: RootState) => state.presentation.sortingState;
export const selectPresentation = (state: RootState) => state.presentation;

export const {
  updateDescription,
  updateAssignments,
  updateLayout,
  updateTileSlotLayoutOrder,
  updateSortingState,
  updateLayoutColsPerRow,
  updateLayoutRowsPerSlide,
  resetLayout,
  toggleCompanyLogoVisibility,
  addAssignmentField,
  replaceAssignmentField,
  toggleTileHeaderData,
  removeAssignmentField,
  updateAssignmentField,
  reset,
  buildStateFromCredByRef,
  buildAssignmentsAndGridTemplate,
  updatePresentationSlideTitle,
  updatePrivateAndConfidential,
  updateCompanyOfficialLogo,
  updatePresentationSlideNumber,
  toggleTilesAlignment,
} = presentationSlice.actions;

export default presentationSlice.reducer;
